import { ChevronRight } from "lucide-react";
import { RuntimeImage, RuntimeLink } from "../../runtime/RendererRuntime";
import type { ImageFieldValue, LinkTarget } from "../../types";
import { resolveLinkHref } from "../../utils/links";
import { getImageObjectPositionStyle, getImageOverrideProps, mergeClassNames, resolveImageValue } from "../../utils/imageOptions";

export type Activity = {
  title: string;
  description?: string;
  href: LinkTarget;
};

type Props = Activity & {
  imageSrc?: ImageFieldValue;
  imageAlt?: string;
  /** Compact variant: hide the description text and collapse the blue panel to
   *  just the title + arrow. Used by image-led tile grids (e.g. the banknote
   *  gallery) where a tall description panel only adds dead space. Default
   *  (non-compact) cards are unchanged. */
  compact?: boolean;
};

/** Activity card with image, title, description and navigation link.
 *
 *  When `imageSrc` is empty, the image strip is omitted and the card becomes
 *  a single text panel — no generic placeholder PNG (which historically read
 *  as a broken image on data-record-style pages without editorial photos).
 *  Editorial pages always supply an image, so the visual is unchanged for
 *  them; only image-less cards get the lean text-only variant.
 *
 *  With `compact`, the description is dropped and the panel loses its
 *  `min-height` so it shrinks to the title + arrow — an image-only tile reads
 *  as a tidy caption strip instead of a half-empty box. */
export function ActivityCard({ title, description, href, imageSrc, imageAlt = "Illustration d’activité", compact = false }: Props) {
  const { src, options } = resolveImageValue(imageSrc);
  const hasImage = Boolean(src);
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const overrideProps = getImageOverrideProps(options);
  const safeHref = resolveLinkHref(href) ?? "/";
  const showDescription = !compact && Boolean(description && description.trim());
  return (
    <RuntimeLink href={safeHref} className="apa-card apa-card-hover group block h-full overflow-hidden">
      <div className={hasImage ? "grid h-full grid-cols-[2fr_3fr] md:flex md:flex-col" : "flex h-full flex-col"}>
        {hasImage ? (
          <div
            className={mergeClassNames(
              "apa-img-editorial relative min-h-[150px] overflow-hidden bg-slate-100 md:min-h-0 md:w-full",
              overrideProps.className
            )}
            style={overrideProps.style}
          >
            <RuntimeImage
              src={src as string}
              alt={imageAlt}
              className="absolute inset-0 h-full w-full object-cover object-[center_30%] transition-transform duration-500 ease-out group-hover:scale-105 md:static md:h-56"
              style={objectPositionStyle}
            />
          </div>
        ) : null}
        {compact ? (
          // Compact: title + arrow on a single row — the chevron is pinned to the
          // right of the title, so there's no spare vertical space.
          <div className="flex items-center justify-between gap-3 px-5 py-4">
            <h3 className="line-clamp-2 text-lg font-semibold leading-6 text-[#0A0A0A] xl:text-xl">
              <span className="apa-underline-reveal">{title}</span>
            </h3>
            <ChevronRight className="h-5 w-5 shrink-0 text-[#00B862] transition-transform group-hover:translate-x-0.5" />
          </div>
        ) : (
          <div className="flex min-h-[150px] flex-col p-4 md:min-h-[180px] md:p-5">
            <div>
              <h3 className="line-clamp-3 text-lg font-semibold leading-6 text-[#0A0A0A] xl:text-xl">
                <span className="apa-underline-reveal">{title}</span>
              </h3>
              {showDescription ? (
                <p className="mt-2 line-clamp-2 text-base leading-relaxed text-[var(--color-muted)] md:line-clamp-3">
                  {description}
                </p>
              ) : null}
            </div>
            <div className="mt-auto pt-3 text-[#00B862]">
              <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </div>
          </div>
        )}
      </div>
    </RuntimeLink>
  );
}


